import { Check, Download, Eye, ImagePlus, Link2, RefreshCw, Upload } from 'lucide-react';
import type { ChangeEvent } from 'react';
import { imagePresets, presets } from '../config';
import type { EditorTab, ImagePreset, StudioConfig, StudioImages } from '../types';
import { ColorField, InspectorSection, RangeField, SelectField, TextField } from './Controls';

interface LeftInspectorProps {
  tab: EditorTab;
  config: StudioConfig;
  images: StudioImages;
  activeImagePresetId: string | null;
  onUpdate: (patch: Partial<StudioConfig>) => void;
  onImage: (slot: keyof StudioImages, file: File) => void;
  onSelectImagePreset: (preset: ImagePreset) => void;
  onSaveImage: (source: string, filename: string, label: string) => void;
  onSwapImages: () => void;
  onCloseMobile: () => void;
}

function UploadSlot({ label, slot, src, onImage }: {
  label: string;
  slot: keyof StudioImages;
  src: string;
  onImage: (slot: keyof StudioImages, file: File) => void;
}) {
  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) onImage(slot, file);
    event.target.value = '';
  };
  return (
    <label className="upload-slot">
      <span className="upload-slot-heading"><strong>{label}</strong><span>{slot.toUpperCase()}</span></span>
      <span className="upload-preview" style={{ backgroundImage: `url("${src}")` }}>
        <span><Upload size={15} />替换图片</span>
      </span>
      <input type="file" accept="image/png,image/jpeg,image/webp,image/avif" onChange={handleChange} />
    </label>
  );
}

export function LeftInspector({ tab, config, images, activeImagePresetId, onUpdate, onImage, onSelectImagePreset, onSaveImage, onSwapImages, onCloseMobile }: LeftInspectorProps) {
  return (
    <aside className="left-inspector inspector-panel" aria-label="内容与素材面板">
      <div className="inspector-titlebar">
        <div>
          <span>{tab === 'content' ? 'Content' : tab === 'media' ? 'Media pair' : 'Visual style'}</span>
          <strong>{tab === 'content' ? '页面内容' : tab === 'media' ? '配对素材' : '视觉样式'}</strong>
        </div>
        <button className="mobile-close" type="button" onClick={onCloseMobile} aria-label="关闭面板">×</button>
      </div>

      <div className="inspector-scroll">
        {tab === 'content' && (
          <>
            <InspectorSection title="文案">
              <TextField label="品牌名" value={config.brand} maxLength={28} onChange={(brand) => onUpdate({ brand })} />
              <TextField label="小标签" value={config.eyebrow} maxLength={42} onChange={(eyebrow) => onUpdate({ eyebrow })} />
              <TextField label="主标题" value={config.title} multiline rows={3} maxLength={72} onChange={(title) => onUpdate({ title })} />
              <TextField label="副标题" value={config.subtitle} maxLength={80} onChange={(subtitle) => onUpdate({ subtitle })} />
              <TextField label="介绍文案" value={config.description} multiline rows={4} maxLength={220} onChange={(description) => onUpdate({ description })} />
              <TextField label="按钮文字" value={config.cta} maxLength={28} onChange={(cta) => onUpdate({ cta })} />
            </InspectorSection>
            <InspectorSection title="快速预设">
              <div className="preset-list">
                {presets.map((preset) => (
                  <button key={preset.name} type="button" onClick={() => onUpdate(preset.patch)}>{preset.name}</button>
                ))}
              </div>
            </InspectorSection>
          </>
        )}

        {tab === 'media' && (
          <>
            <InspectorSection title="内置蒙版预设">
              <p className="section-help"><ImagePlus size={14} />选择一组成对素材载入预览；A、B 原图也可单独保存。</p>
              <div className="image-preset-list">
                {imagePresets.map((preset) => {
                  const selected = activeImagePresetId === preset.id;
                  return (
                    <article key={preset.id} className={`image-preset-card ${selected ? 'selected' : ''}`}>
                      <div className="image-preset-visual" aria-hidden="true">
                        <span style={{ backgroundImage: `url("${preset.images.a}")` }}><b>A</b></span>
                        <span style={{ backgroundImage: `url("${preset.images.b}")` }}><b>B</b></span>
                      </div>
                      <div className="image-preset-copy">
                        <div><strong>{preset.name}</strong>{selected && <span><Check size={11} />已选</span>}</div>
                        <small>{preset.description}</small>
                      </div>
                      <div className="image-preset-actions">
                        <button type="button" className="preset-preview-button" aria-label={`预览 ${preset.name}`} aria-pressed={selected} onClick={() => onSelectImagePreset(preset)}>
                          <Eye size={13} />预览
                        </button>
                        <button type="button" aria-label={`保存 ${preset.name} 图片 A`} onClick={() => onSaveImage(preset.images.a, preset.filenames.a, `${preset.name} A`)}>
                          <Download size={13} />保存 A
                        </button>
                        <button type="button" aria-label={`保存 ${preset.name} 图片 B`} onClick={() => onSaveImage(preset.images.b, preset.filenames.b, `${preset.name} B`)}>
                          <Download size={13} />保存 B
                        </button>
                      </div>
                    </article>
                  );
                })}
              </div>
            </InspectorSection>

            <InspectorSection
              title="两张主图"
              actions={<button className="section-action" type="button" onClick={onSwapImages}><RefreshCw size={13} />交换</button>}
            >
              <p className="section-help"><ImagePlus size={14} />同尺寸、同视角素材能得到最自然的显隐关系。</p>
              <UploadSlot label="状态 A / 基底" slot="a" src={images.a} onImage={onImage} />
              <UploadSlot label="状态 B / 揭示" slot="b" src={images.b} onImage={onImage} />
            </InspectorSection>

            <InspectorSection title="构图配准" actions={<Link2 size={14} aria-label="两张图片共享相同坐标系" />}>
              <div className="alignment-group">
                <strong>图片 A</strong>
                <RangeField label="缩放" value={config.imageAScale} min={1} max={1.5} step={0.01} unit="×" onChange={(imageAScale) => onUpdate({ imageAScale })} />
                <RangeField label="水平焦点" value={config.imageAFocalX} min={0} max={100} unit="%" onChange={(imageAFocalX) => onUpdate({ imageAFocalX })} />
                <RangeField label="垂直焦点" value={config.imageAFocalY} min={0} max={100} unit="%" onChange={(imageAFocalY) => onUpdate({ imageAFocalY })} />
              </div>
              <div className="alignment-group">
                <strong>图片 B</strong>
                <RangeField label="缩放" value={config.imageBScale} min={1} max={1.5} step={0.01} unit="×" onChange={(imageBScale) => onUpdate({ imageBScale })} />
                <RangeField label="水平焦点" value={config.imageBFocalX} min={0} max={100} unit="%" onChange={(imageBFocalX) => onUpdate({ imageBFocalX })} />
                <RangeField label="垂直焦点" value={config.imageBFocalY} min={0} max={100} unit="%" onChange={(imageBFocalY) => onUpdate({ imageBFocalY })} />
              </div>
            </InspectorSection>
          </>
        )}

        {tab === 'style' && (
          <>
            <InspectorSection title="色彩">
              <ColorField label="主色" value={config.primary} onChange={(primary) => onUpdate({ primary })} />
              <ColorField label="强调色" value={config.accent} onChange={(accent) => onUpdate({ accent })} />
              <ColorField label="背景色" value={config.background} onChange={(background) => onUpdate({ background })} />
            </InspectorSection>
            <InspectorSection title="排版与画幅">
              <RangeField label="标题字号" value={config.titleSize} min={44} max={96} unit="px" onChange={(titleSize) => onUpdate({ titleSize })} />
              <SelectField label="字体气质" value={config.fontStyle} options={[
                { value: 'grotesk', label: '现代无衬线' },
                { value: 'editorial', label: '编辑感衬线' },
                { value: 'mono', label: '档案等宽体' },
              ]} onChange={(fontStyle) => onUpdate({ fontStyle })} />
              <SelectField label="图片适配" value={config.fit} options={[
                { value: 'cover', label: '铺满裁切' },
                { value: 'contain', label: '完整显示' },
              ]} onChange={(fit) => onUpdate({ fit })} />
            </InspectorSection>
            <InspectorSection title="影像调色">
              <RangeField label="亮度" value={config.brightness} min={50} max={140} unit="%" onChange={(brightness) => onUpdate({ brightness })} />
              <RangeField label="对比度" value={config.contrast} min={60} max={160} unit="%" onChange={(contrast) => onUpdate({ contrast })} />
              <RangeField label="饱和度" value={config.saturation} min={0} max={180} unit="%" onChange={(saturation) => onUpdate({ saturation })} />
              <RangeField label="模糊" value={config.blur} min={0} max={8} unit="px" onChange={(blur) => onUpdate({ blur })} />
              <RangeField label="暗角" value={config.vignette} min={0} max={90} unit="%" onChange={(vignette) => onUpdate({ vignette })} />
            </InspectorSection>
          </>
        )}
      </div>
    </aside>
  );
}
